import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const ROLE_OPTIONS = [
  { value: 'BROKER_ADMIN', label: 'Broker Admin', color: '#dc2626' },
  { value: 'BROKER_AGENT', label: 'Broker Agent', color: '#3b82f6' },
  { value: 'ACCOUNTING', label: 'Accounting', color: '#10b981' },
  { value: 'SHIPPER', label: 'Shipper', color: '#8b5cf6' },
  { value: 'SHIPPER_USER', label: 'Shipper', color: '#8b5cf6' },
  { value: 'CARRIER', label: 'Carrier', color: '#f59e0b' },
  { value: 'CARRIER_USER', label: 'Carrier', color: '#f59e0b' }
];

function getRoleDisplay(role) {
  const found = ROLE_OPTIONS.find(r => r.value === role);
  return found || { label: role, color: '#6b7280' };
}

export default function AdminUsers() {
  const { adminEmail } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'BROKER_AGENT', password: '' });

  async function loadUsers() {
    setLoading(true);
    try {
      const [usersRes, orgRes] = await Promise.all([
        fetch('/api/broker/users', { headers: { 'x-admin-email': adminEmail } }),
        fetch('/api/organization/my-org', { headers: { 'x-admin-email': adminEmail } })
      ]);
      const usersData = await usersRes.json();
      const orgData = await orgRes.json();
      setUsers(usersData.users || []);
      setOrgName(orgData.organization?.name || '');
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUsers(); }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: '', name: '', role: 'BROKER_AGENT', password: '' });
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ email: user.email, name: user.name || '', role: user.role, password: '' });
    setShowModal(true);
  };

  const saveUser = async () => {
    try {
      const url = editUser ? `/api/broker/users/${editUser.id}` : '/api/broker/users';
      const method = editUser ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      
      setShowModal(false);
      loadUsers();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try {
      const res = await fetch(`/api/broker/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'x-admin-email': adminEmail }
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      loadUsers();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 28, fontWeight: 900 }}>👤 User Management</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Manage users for <strong>{orgName}</strong> • {users.length} users
          </p>
        </div>
        <button onClick={openCreate} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          + Create User
        </button>
      </div>

      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 120px 100px 120px', gap: 16, padding: 16, background: '#f9fafb', fontWeight: 700, fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>
          <div>Email</div>
          <div>Name</div>
          <div>Role</div>
          <div>Created</div>
          <div>Actions</div>
        </div>
        
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No users found</div>
        ) : (
          users.map(user => {
            const roleInfo = getRoleDisplay(user.role);
            return (
              <div key={user.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 120px 100px 120px', gap: 16, padding: 16, borderTop: '1px solid #f3f4f6', alignItems: 'center' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{user.email}</div>
                <div style={{ color: '#374151' }}>{user.name || '—'}</div>
                <div>
                  <span style={{ 
                    padding: '4px 10px', 
                    borderRadius: 12, 
                    background: `${roleInfo.color}15`, 
                    color: roleInfo.color, 
                    fontSize: 11, 
                    fontWeight: 700 
                  }}>
                    {roleInfo.label}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(user)} style={{ padding: '6px 12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                    Edit
                  </button>
                  <button onClick={() => deleteUser(user)} style={{ padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 450 }}>
            <h2 style={{ margin: '0 0 20px 0' }}>{editUser ? 'Edit User' : 'Create User'}</h2>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Email *</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editUser} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb', background: editUser ? '#f9fafb' : 'white' }} />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Role *</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }}>
                  <optgroup label="Broker Roles">
                    <option value="BROKER_ADMIN">Broker Admin</option>
                    <option value="BROKER_AGENT">Broker Agent</option>
                    <option value="ACCOUNTING">Accounting</option>
                  </optgroup>
                  <optgroup label="External Roles">
                    <option value="SHIPPER">Shipper</option>
                    <option value="CARRIER">Carrier</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{editUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #e5e7eb' }} />
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: 'white', border: '2px solid #e5e7eb', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={saveUser} style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
